import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useBranding } from "../context/BrandingContext";
import axios from "axios";
import {
  LayoutDashboard, MessageSquare, Globe, Bot, BarChart3,
  Settings, LogOut, ChevronLeft, ChevronRight, Globe2, Bell, X, Volume2, Menu, Moon, Sun
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOUND_URL = "/sounds/notification.mp3";

function playNotifSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = freq;
      o.type = 'sine';
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.25, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + dur);
      o.start(start); o.stop(start + dur);
    };
    const t = ctx.currentTime;
    play(880, t, 0.18);
    play(1100, t + 0.18, 0.22);
  } catch (e) {}
}
const navItems = [
  { key: "dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { key: "inbox", icon: MessageSquare, path: "/inbox" },
  { key: "sites", icon: Globe, path: "/sites" },
  { key: "ai", icon: Bot, path: "/ai" },
  { key: "analytics", icon: BarChart3, path: "/analytics" },
  { key: "settings", icon: Settings, path: "/settings" },
];

export default function Layout({ children }) {
  const { user, logout, token } = useAuth();
  const { t, lang, switchLang } = useLang();
  const branding = useBranding();
  const [collapsed, setCollapsed] = useState(false);
  const [dark, setDark] = useState(() => {
    try { return document.documentElement.classList.contains("dark"); } catch { return false; }
  });
  const toggleTheme = () => {
    setDark((d) => {
      const nd = !d;
      try {
        document.documentElement.classList.toggle("dark", nd);
        localStorage.setItem("cd_theme", nd ? "dark" : "light");
      } catch {}
      return nd;
    });
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const [waitingNotif, setWaitingNotif] = useState(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const seenIdsRef = useRef(new Set());
  const activeWaitingRef = useRef(new Set()); // currently waiting conv IDs
  const audioRef = useRef(null);
  const audioLoopRef = useRef(null);

  // Request browser notification permission
  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const result = await Notification.requestPermission();
      setNotifPermission(result);
    } catch (e) {}
  };

  // Show browser notification (when tab not visible)
  const showBrowserNotif = useCallback((conv) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return;
    try {
      const n = new Notification("Temsilci Talebi!", {
        body: `${conv.visitor_name || "Ziyaretçi"} temsilci bekliyor`,
        icon: "/favicon.ico",
        tag: "waiting-agent",
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        navigate(`/inbox?conv=${conv.id}`);
        n.close();
      };
    } catch (e) {}
  }, [navigate]);

  const stopSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (audioLoopRef.current) {
      clearInterval(audioLoopRef.current);
      audioLoopRef.current = null;
    }
  }, []);

  const startSound = useCallback(() => {
    stopSound();
    const playOnce = () => {
      try {
        const audio = new Audio(SOUND_URL);
        audioRef.current = audio;
        audio.volume = 0.85;
        audio.play().catch(() => {});
      } catch (e) {}
    };
    playOnce();
    // Repeat every 9 seconds while any conv is waiting_agent
    audioLoopRef.current = setInterval(() => {
      if (activeWaitingRef.current.size > 0) {
        playOnce();
      } else {
        stopSound();
      }
    }, 3000);
  }, [stopSound]);

  // Global polling: "Temsilci Bekliyor" conversations
  useEffect(() => {
    if (!token) return;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/conversations?status=waiting_agent`);
        const currentIds = new Set(data.map(c => c.id));

        // New ones that weren't seen before
        const newOnes = data.filter(c => !seenIdsRef.current.has(c.id));

        // Update active waiting set
        activeWaitingRef.current = currentIds;

        if (newOnes.length > 0) {
          setWaitingNotif(newOnes[0]);
          newOnes.forEach(c => seenIdsRef.current.add(c.id));
          startSound();
          // Browser notification when tab is not focused
          showBrowserNotif(newOnes[0]);
        }

        // Stop sound + banner if no more waiting conversations
        if (currentIds.size === 0) {
          stopSound();
          setWaitingNotif(null);
        } else {
          // Banner'daki konuşma artık waiting_agent değilse banner'ı kapat
          setWaitingNotif(prev => {
            if (prev && !currentIds.has(prev.id)) return null;
            return prev;
          });
        }
      } catch {}
    };
    check();
    const iv = setInterval(check, 5000);
    return () => { clearInterval(iv); stopSound(); };
  }, [token, startSound, stopSound, showBrowserNotif]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 pt-14 md:pt-0">
      {/* Waiting agent notification banner */}
      {waitingNotif && (
        <div className="fixed top-16 md:top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-orange-500 text-white px-5 py-3 rounded-2xl shadow-xl shadow-orange-500/30 max-w-sm w-[90%] md:w-auto" style={{animation:'slideDown .4s cubic-bezier(.34,1.2,.64,1)'}}>
          <Bell size={18} className="flex-shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Temsilci Talebi!</div>
            <div className="text-xs text-orange-100 truncate">{waitingNotif.visitor_name || "Ziyaretçi"} temsilci bekliyor</div>
          </div>
          <button onClick={() => { navigate(`/inbox?conv=${waitingNotif.id}`); setWaitingNotif(null); stopSound(); }} className="px-3 py-1 bg-white/20 rounded-lg text-xs font-medium hover:bg-white/30 transition-colors flex-shrink-0">Aç</button>
          <button onClick={() => { setWaitingNotif(null); stopSound(); }} className="text-orange-200 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* Browser notification permission prompt */}
      {notifPermission === "default" && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-800 text-white px-4 py-3 rounded-2xl shadow-xl max-w-sm w-[90%] md:w-auto border border-slate-700">
          <Bell size={16} className="flex-shrink-0 text-blue-400" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">Tarayıcı Bildirimleri</div>
            <div className="text-xs text-slate-400">Sekme dışındayken bildirim almak için izin verin</div>
          </div>
          <button onClick={requestNotifPermission} className="px-3 py-1.5 bg-blue-600 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors flex-shrink-0">İzin Ver</button>
          <button onClick={() => setNotifPermission("denied")} className="text-slate-400 hover:text-white"><X size={13} /></button>
        </div>
      )}

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 flex items-center justify-between px-4 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-slate-700" data-testid="mobile-menu-btn">
            <Menu size={18} />
          </button>
          <img src={branding.logo_url} alt={branding.brand_name} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
          <span className="font-bold text-white text-sm">{branding.brand_name}</span>
        </div>
        {user && (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative inset-y-0 left-0 z-50 flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        style={{ width: collapsed ? "64px" : "240px", background: "#0f172a", flexShrink: 0 }}
        data-testid="sidebar"
      >
        {/* Mobile close */}
        <button className="md:hidden absolute top-3 right-3 w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-slate-700 z-10" onClick={() => setMobileOpen(false)}>
          <X size={16} />
        </button>
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-800">
          <img src={branding.logo_url} alt={branding.brand_name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
          {!collapsed && (
            <span className="font-heading font-bold text-white text-base truncate">
              {branding.brand_name}
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {navItems.map(({ key, icon: Icon, path }) => (
            <NavLink
              key={key}
              to={path}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`
              }
              title={collapsed ? t(`nav.${key}`) : undefined}
              data-testid={`nav-${key}`}
            >
              <Icon size={18} />
              {!collapsed && <span>{t(`nav.${key}`)}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="px-3 py-4 border-t border-slate-800 space-y-1">
          {/* Lang toggle */}
          <button
            onClick={() => switchLang(lang === "tr" ? "en" : "tr")}
            className={`sidebar-item w-full ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="lang-toggle"
            title="Language / Dil"
          >
            <Globe2 size={18} />
            {!collapsed && <span>{lang === "tr" ? "English" : "Türkçe"}</span>}
          </button>

          {/* Tema (karanlık/aydınlık mod) */}
          <button
            onClick={toggleTheme}
            className={`sidebar-item w-full ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="theme-toggle"
            title="Tema"
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
            {!collapsed && <span>{dark ? "Aydınlık Mod" : "Karanlık Mod"}</span>}
          </button>

          {/* User */}
          {!collapsed && (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-300 text-xs font-medium truncate">{user?.name}</div>
                <div className="text-slate-500 text-xs truncate">{user?.role}</div>
              </div>
            </div>
          )}

          <button
            onClick={handleLogout}
            className={`sidebar-item w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 ${collapsed ? "justify-center px-0" : ""}`}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {!collapsed && <span>{t("nav.logout")}</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-slate-300 hover:bg-blue-600 hover:text-white transition-colors z-10"
          data-testid="sidebar-collapse"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
